import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Table, Modal, Button, Combobox, DateInput } from '../../../components/common';
import { apiError } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { sortByLabel } from '../../../config/columns';
import { uploadDocumentService, customerService } from '../../../services/resources';

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + '01';

const emptyForm = () => ({
  transaction_date: today(),
  customer_id: '',
  remark: '',
  idempotency_key: '',
});

export default function UploadDocumentPage({ onToast }) {
  const { can } = useAuth();
  const canView = can('UPLOAD_DOCUMENT', 'CanView');
  const canCreate = can('UPLOAD_DOCUMENT', 'CanCreate');
  const canDelete = can('UPLOAD_DOCUMENT', 'CanDelete');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());

  const [customers, setCustomers] = useState([]);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async (from = dateFrom, to = dateTo) => {
    setLoading(true);
    try {
      setRows(await uploadDocumentService.list(from, to));
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const changeDateFrom = (v) => {
    setDateFrom(v);
    load(v, dateTo);
  };
  const changeDateTo = (v) => {
    setDateTo(v);
    load(dateFrom, v);
  };

  useEffect(() => {
    load();
    customerService.list().then(setCustomers).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const customerOptions = sortByLabel(customers.map((c) => ({ value: c.id, label: c.customer_name })));

  const set = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => {
      if (!prev[k]) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const keys = ['customer_name', 'remark', 'file_name'];
    return rows.filter((r) => keys.some((k) => String(r[k] ?? '').toLowerCase().includes(term)));
  }, [rows, search]);

  const openCreate = () => {
    setForm({ ...emptyForm(), idempotency_key: crypto.randomUUID() });
    setFile(null);
    setErrors({});
    setSubmitError('');
    setOpen(true);
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && f.type !== 'application/pdf') {
      setErrors((prev) => ({ ...prev, file: 'Only PDF files are allowed' }));
      setFile(null);
      return;
    }
    if (f && f.size > 10 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, file: 'File must be 10MB or smaller' }));
      setFile(null);
      return;
    }
    setErrors((prev) => {
      const next = { ...prev };
      delete next.file;
      return next;
    });
    setFile(f);
  };

  const validate = () => {
    const e = {};
    if (!form.transaction_date) e.transaction_date = 'Date is required';
    if (!form.customer_id) e.customer_id = 'Customer is required';
    if (!file) e.file = 'A PDF file is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (savingRef.current) return;
    if (!validate()) return;
    savingRef.current = true;
    setSaving(true);
    setSubmitError('');
    const payload = {
      transaction_date: form.transaction_date,
      customer_id: Number(form.customer_id),
      remark: form.remark || '',
      idempotency_key: form.idempotency_key,
    };
    try {
      await uploadDocumentService.create(payload, file);
      onToast?.('Document uploaded');
      setOpen(false);
      load();
    } catch (err) {
      setSubmitError(apiError(err).message);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await uploadDocumentService.remove(deleteTarget.id);
      onToast?.('Document deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    }
  };

  const doDownload = async (row) => {
    try {
      await uploadDocumentService.download(row.id, row.file_name);
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    }
  };

  if (!canView) {
    return (
      <Card title="Upload Document">
        <p className="py-6 text-center text-sm text-gray-500">
          You don’t have permission to view this page.
        </p>
      </Card>
    );
  }

  const columns = [
    { key: 'transaction_date', label: 'Date', render: (r) => String(r.transaction_date).slice(0, 10) },
    { key: 'customer_name', label: 'Customer' },
    { key: 'remark', label: 'Remark' },
    { key: 'file_name', label: 'File' },
    {
      key: '__a',
      label: 'Actions',
      className: 'text-right whitespace-nowrap',
      render: (r) => (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="secondary" onClick={() => doDownload(r)}>
            Download
          </Button>
          {canDelete && (
            <Button size="sm" variant="danger" onClick={() => setDeleteTarget(r)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Card title="Upload Document" actions={canCreate ? <Button onClick={openCreate}>+ New</Button> : null}>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <DateInput label="Date From" value={dateFrom} onChange={changeDateFrom} />
        </div>
        <div className="w-40">
          <DateInput label="Date To" value={dateTo} onChange={changeDateTo} />
        </div>
        <div className="flex flex-1 items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          />
          {search && (
            <Button type="button" variant="ghost" onClick={() => setSearch('')}>
              Clear
            </Button>
          )}
        </div>
      </div>

      <Table
        columns={columns}
        rows={filtered}
        loading={loading}
        maxHeight="60vh"
        className="rounded-md border border-gray-100"
      />
      <p className="mt-2 text-xs text-gray-400">{filtered.length} document(s)</p>

      {/* Upload modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="New Document"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={submit} className="space-y-3">
          {submitError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</div>
          )}
          <DateInput
            label="Date"
            required
            value={form.transaction_date}
            error={errors.transaction_date}
            onChange={(v) => set('transaction_date', v)}
          />
          <Combobox
            label="Customer"
            required
            placeholder="-- select customer --"
            options={customerOptions}
            value={form.customer_id}
            error={errors.customer_id}
            onChange={(v) => set('customer_id', v)}
          />
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Remark</span>
            <textarea
              value={form.remark}
              onChange={(e) => set('remark', e.target.value)}
              style={{ resize: 'none' }}
              className="h-16 w-full overflow-y-auto rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              File (PDF) <span className="text-red-500">*</span>
            </span>
            <input
              type="file"
              accept="application/pdf"
              onChange={onFileChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-sm"
            />
            {errors.file && <span className="mt-1 block text-xs text-red-600">{errors.file}</span>}
          </label>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Document"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">This will be permanently deleted. Continue?</p>
      </Modal>
    </Card>
  );
}
